import React, { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { CKEditor } from 'ckeditor4-react';
import { TextTiny, palette } from '@awarego-ui/components';

/** OSS CKEditor 4 (full-all includes Source). Same era as GoPhish fullPage editing. */
export const CKEDITOR4_EDITOR_URL =
    'https://cdn.ckeditor.com/4.22.1/full-all/ckeditor.js';

const Wrap = styled.div`
    margin-top: 4px;

    .cke {
        border-color: ${palette.graphite.silver} !important;
    }

    .cke_contents {
        max-height: 420px;
    }
`;

const EDITOR_CONFIG = {
    fullPage: true,
    allowedContent: true,
    height: 360,
    // Start in source so forms/CSS are obvious; Source button toggles WYSIWYG.
    startupMode: 'source',
    removePlugins: 'exportpdf',
    versionCheck: false,
    toolbar: [
        {
            name: 'document',
            items: ['Source', '-', 'NewPage', 'Preview'],
        },
        {
            name: 'clipboard',
            items: [
                'Cut',
                'Copy',
                'Paste',
                'PasteText',
                'PasteFromWord',
                '-',
                'Undo',
                'Redo',
            ],
        },
        {
            name: 'basicstyles',
            items: [
                'Bold',
                'Italic',
                'Underline',
                'Strike',
                '-',
                'RemoveFormat',
            ],
        },
        {
            name: 'paragraph',
            items: [
                'NumberedList',
                'BulletedList',
                '-',
                'Outdent',
                'Indent',
                '-',
                'Blockquote',
            ],
        },
        { name: 'links', items: ['Link', 'Unlink'] },
        { name: 'insert', items: ['Image', 'Table', 'HorizontalRule'] },
        { name: 'styles', items: ['Format', 'Font', 'FontSize'] },
        { name: 'colors', items: ['TextColor', 'BGColor'] },
        { name: 'tools', items: ['Maximize'] },
    ],
};

/**
 * Landing-page HTML editor (CKEditor 4 fullPage — GoPhish-style).
 * Preserves full documents, forms, and styles. Source mode is default.
 */
export default function LandingPageHtmlEditor({
    value,
    onChange,
    editorKey = 0,
}) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const handleChange = useCallback((evt) => {
        const editor = evt && evt.editor;
        if (!editor || !onChangeRef.current) return;
        onChangeRef.current(editor.getData());
    }, []);

    const initData = useMemo(() => value || '', [editorKey]);

    return (
        <Wrap>
            <TextTiny lighter style={{ marginBottom: 6, display: 'block' }}>
                Full-page editor. Use Source for raw HTML; Visual keeps forms
                and styles.
            </TextTiny>
            <CKEditor
                key={editorKey}
                editorUrl={CKEDITOR4_EDITOR_URL}
                initData={initData}
                config={EDITOR_CONFIG}
                onChange={handleChange}
                onBlur={handleChange}
            />
        </Wrap>
    );
}
